import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ChatAlert } from './ChatAlert.tsx';
import { getToolUnavailableKey, isToolOutputError, parseToolCalls } from './toolDisplay.tsx';
import Button from '../../core/components/Button.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { url } from '../../core/utils/link.ts';
import { CHAT_PATH } from '../constants.ts';
import { useChatUsage } from '../hooks/useChatUsage.ts';
import type { ChatErrorKind } from '../hooks/useConversationStream.ts';

const InfoIcon = withIcon(faInfoCircle);

const RETRYABLE: ChatErrorKind[] = ['sendFailed', 'connectionLost', 'unavailable'];

export const ChatErrorNotice = ({
    kind,
    onRetry,
    onEdit = null,
}: {
    kind: ChatErrorKind;
    onRetry: () => void;
    onEdit?: (() => void) | null;
}) => {
    const { t } = useTranslation('chat');

    if (kind === 'contentFilter') {
        return (
            <div className="mb-5 flex flex-col items-start gap-3 text-sm text-content-strong">
                <p className="m-0">{t('thread.error.contentFilter.text')}</p>
                <div className="flex items-start gap-2 text-xs text-content-medium">
                    <InfoIcon className="mt-0.5 flex-none" />
                    <span>{t('thread.error.contentFilter.hint')}</span>
                </div>
                {onEdit && <Button onClick={onEdit}>{t('thread.editPrompt')}</Button>}
            </div>
        );
    }

    const canRetry = RETRYABLE.includes(kind);
    return (
        <ChatAlert
            className="mb-5"
            severity="warning"
            title={t(`thread.error.${kind}.title`)}
            text={t(`thread.error.${kind}.text`)}
            actions={
                canRetry && (
                    <>
                        <Button onClick={onRetry}>{t('thread.retry')}</Button>
                        {onEdit && <Button onClick={onEdit}>{t('thread.editPrompt')}</Button>}
                    </>
                )
            }
        />
    );
};

export const CostLimitNotice = () => {
    const { t } = useTranslation('chat');
    const { resetDate } = useChatUsage();
    return (
        <ChatAlert
            severity="error"
            title={t('thread.costLimit.title')}
            text={t('thread.costLimit.text', { date: resetDate })}
        />
    );
};

export const ChatFullNotice = ({ question }: { question: string | null }) => {
    const { t } = useTranslation('chat');
    const navigate = useNavigate();
    return (
        <ChatAlert
            severity="error"
            title={t('thread.chatFull.title')}
            text={t('thread.chatFull.text')}
            actions={
                <Button onClick={() => navigate(url(CHAT_PATH), { state: question ? { fillPrompt: question } : {} })}>
                    {t('thread.chatFull.action')}
                </Button>
            }
        />
    );
};

export const OfflineNotice = () => {
    const { t } = useTranslation('chat');
    return <ChatAlert severity="warning" title={t('thread.offline.title')} text={t('thread.offline.text')} />;
};

export const ToolFailureNotice = ({ toolCalls, onRetry }: { toolCalls: unknown; onRetry: () => void }) => {
    const { t } = useTranslation('chat');
    const failed = parseToolCalls(toolCalls).filter((toolCall) => isToolOutputError(toolCall.tool_output));
    if (!failed.length) return null;

    const excelOnly = failed.every(
        (toolCall) => getToolUnavailableKey(toolCall.tool_name || '') === 'thread.tools.unavailableExcel'
    );
    const key = excelOnly ? 'thread.excelFailed' : 'thread.partial';
    return (
        <ChatAlert
            className="mb-5"
            severity="warning"
            title={t(`${key}.title`)}
            text={t(`${key}.text`)}
            actions={<Button onClick={onRetry}>{t(excelOnly ? 'thread.excelFailed.action' : 'thread.retry')}</Button>}
        />
    );
};
